import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import type { INewPaymentData } from "components/shared/PaymentModal";
import { downloadApiExport, downloadFile, getDateForFileName, isFinalReq } from "helpers/utils";
import { translationsWithFallback } from "helpers/utils";
import { t } from "i18next";
import { toast } from "react-toastify";
import type { CalculationBaseColumn } from "store/calculationBaseColumnLabelsStore";
import type { TAccountStatus } from "types";
import type { Binary } from "types";

import type { DTOCreateAdvancePaymentSlice } from "./advancePaymentSlice";
import type { TEntity } from "./beneficiaryStore";
import { fetchPeriods } from "./periodStore";
import type { Currencies } from "./settingsStore";
import type { AccountingMapping, TFetchStatus, TParamsPagination } from "./types";
import { PaymentStatus } from "./types";

export const getGeneralTabData = createAsyncThunk(
    "management/getGeneralTabData",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/management", {
                params: options,
                timeout: 20_000,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data ?? { message: "ServerError.InternalServerError" });
        }
    },
);

export const fetchExportFile = createAsyncThunk(
    "management/fetchExportFile",
    async (periodId: number, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/management/export?periodId=${periodId}`, {
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `export_period_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchExportBeneficiariesFile = createAsyncThunk(
    "management/fetchExportFile",
    async (periodId: number, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(
                    import.meta.env.REACT_APP_API_ENDPOINT + `/management/beneficiary/export?periodId=${periodId}`,
                    {
                        headers: { Accept: "*/*" },
                    },
                ),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );
            const fileName = `export_period_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchExportStocks = createAsyncThunk(
    "management/fetchExportStocks",
    async (periodId: number, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/stock/export?periodId=${periodId}`, {
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );
            const fileName = `export_stock_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchExportSales = createAsyncThunk(
    "management/fetchExportSales",
    async ({ startDate, endDate }: { startDate?: string; endDate?: string } = {}, { rejectWithValue }) => {
        const toastId = toast.loading(`${t("Export.Pending")}`);

        try {
            const params = new URLSearchParams();
            if (startDate) params.append("startDate", startDate);
            if (endDate) params.append("endDate", endDate);

            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT +
                    `/sales/export${params.toString() ? `?${params.toString()}` : ""}`,
                {
                    headers: { Accept: "*/*" },
                },
            );

            toast.update(toastId, {
                render: `${t("Export.Successful")}`,
                type: "success",
                isLoading: false,
                autoClose: 5000,
            });

            const fileName = `export_sales_${getDateForFileName()}.csv`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            let errorMessage = `${t("Export.Error")}`;

            if (error?.response?.data?.message) {
                errorMessage = translationsWithFallback(`Export.${error.response.data.message}`, errorMessage);
            }

            toast.update(toastId, {
                render: errorMessage,
                type: "error",
                isLoading: false,
                autoClose: 5000,
            });

            return rejectWithValue(error?.response?.data);
        }
    },
);

export const fetchExportPackageSales = createAsyncThunk(
    "management/fetchExportPackageSales",
    async (_, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/sales/export-package`, {
                    headers: { Accept: "*/*" },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                    error: `${t("Export.Error")}`,
                },
            );

            const fileName = `export_package_${getDateForFileName()}.xlsx`;
            downloadApiExport(response.data, fileName);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchSalesByChannel = createAsyncThunk(
    "management/fetchSalesByChannel",
    async (
        {
            options,
            termId,
            titleId,
            salesScope,
        }: { options: TParamsPagination; termId: number; titleId?: number; salesScope?: "term" },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/term/${termId}/sales`, {
                params: {
                    ...options,
                    ...(titleId != null ? { titleId } : {}),
                    ...(salesScope ? { salesScope } : {}),
                },
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchBeneficiariesInformation = createAsyncThunk(
    "management/fetchBeneficiariesInformation",
    async (options: TParamsPagination, { rejectWithValue }) => {
        try {
            const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + "/management/beneficiary", {
                params: options,
            });
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchUnpaidBeneficiariesInformation = createAsyncThunk(
    "management/fetchUnpaidBeneficiariesInformation",
    async (periodId: number, { rejectWithValue }) => {
        try {
            const response = await axios.get(
                import.meta.env.REACT_APP_API_ENDPOINT +
                    `/management/beneficiary?periodId=${periodId}&sortBy=grossDuty&sortOrder=DESC&grossDuty=>0&paidStatus=unpaid`,
            );
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchAddSales = createAsyncThunk(
    "management/fetchAddSales",
    async (req: TAddSales, { rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + `/sales`, req);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchDeleteSales = createAsyncThunk(
    "management/fetchDeleteSales",
    async (req: TDeleteSales, { rejectWithValue }) => {
        try {
            const response = await axios.delete(import.meta.env.REACT_APP_API_ENDPOINT + `/sales/${req.salesId}`, {
                params: {
                    periodId: req.periodId,
                    channelId: req.channelId,
                    termId: req.termId,
                    ...(req.titleId != null ? { titleId: req.titleId } : {}),
                },
            });
            return { data: response.data, id: req.salesId };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const updateSale = createAsyncThunk("management/updateSale", async (req: TUpdateSale, { rejectWithValue }) => {
    try {
        const response = await axios.patch(
            import.meta.env.REACT_APP_API_ENDPOINT + `/sales/${req.salesId}`,
            { note: req.note },
            {
                params: {
                    channelId: req.channelId,
                    ...(req.titleId != null ? { titleId: req.titleId } : {}),
                },
            },
        );
        return { data: response.data, salesId: req.salesId, note: req.note };
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});

export const fetchCheckAddingSales = createAsyncThunk<
    { isPeriodClosed: boolean },
    { titleId?: number | null; periodId: number; termId: number }
>("management/checkAddSales", async ({ titleId, periodId, termId }, { rejectWithValue }) => {
    try {
        const { data: { data: result } = { data: {} } } = await axios.post(
            import.meta.env.REACT_APP_API_ENDPOINT + `/sales/getSaleEligibility/${titleId ?? 0}`,
            { periodId, termId },
        );

        if (!result.canAddSales) {
            throw new Error("Cannot add sales");
        }
        return { isPeriodClosed: result.isPeriodClosed ?? false };
    } catch (error: any) {
        return rejectWithValue(error?.response?.data);
    }
});
export const updatePaymentStatus = createAsyncThunk(
    "management/changePaymentStatus",
    async (
        { periodId, id, status, paymentDate }: { periodId: number; id: number; status: number; paymentDate: Date },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/management/beneficiary/${id}/paymentStatus`,
                {
                    periodId,
                    id: id,
                    status: status,
                    paymentDate,
                },
            );
            return { data: response.data, id, status };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const recomputeTermRoyalties = createAsyncThunk(
    "management/recomputeTermRoyalties",
    async ({ periodId, termId }: { periodId: number; termId: number }, { rejectWithValue }) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/period/${periodId}/term/${termId}/recompute-royalties`,
            );
            return response.data.data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export interface DTOUpdateBalance {
    amount: number;
    periodId: number;
    comment?: string | null;
    throughAdvance?: number;
    slices?: DTOCreateAdvancePaymentSlice[];
}

/**
 * Updates N-1 balance on current period.
 * Creates related advance if asked.
 */
export const updateInitialBalance = createAsyncThunk(
    "management/updateInitialBalance",
    async (
        {
            data,
            termId,
            isOriginalPeriodClosed,
        }: {
            data: DTOUpdateBalance;
            termId: number;
            /**
             * Used below to update state.
             */
            isOriginalPeriodClosed: boolean;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/management/balance/${termId}`,
                { data: { data } },
            );
            return response.data.data;
        } catch (e: any) {
            return rejectWithValue(e.response.data);
        }
    },
);

export const updatePaymentStatusBulk = createAsyncThunk(
    "manage/updatePaymentStatusBulk",
    async (
        { periodId, id, status, paymentDate }: { periodId: number; id: number[]; status: number; paymentDate: Date },
        { rejectWithValue },
    ) => {
        try {
            const response = await axios.post(
                import.meta.env.REACT_APP_API_ENDPOINT + `/management/beneficiary/paymentStatus/bulk`,
                {
                    periodId,
                    id,
                    status: status,
                    paymentDate,
                },
            );
            return { data: response.data, id, status };
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);
export const fetchStats = createAsyncThunk("management/fetchStats", async (periodId: number, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/management/stats`, {
            params: { periodId },
            timeout: 20_000,
        });
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response?.data ?? { message: "ServerError.InternalServerError" });
    }
});
export const fetchPressStats = createAsyncThunk("management/fetchPressStats", async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/management/stats`);
        return response.data.result;
    } catch (error: any) {
        return rejectWithValue(error.response.data);
    }
});
export const fetchSendStatement = createAsyncThunk(
    "management/fetchSendStatements",
    async (
        {
            terms,
            periodId,
            articles = [],
            host,
        }: {
            terms?: number[];
            periodId?: number;
            articles?: number[];
            host: string;
        },
        { dispatch, rejectWithValue },
    ) => {
        try {
            const params = articles.length > 0 ? { articles, host } : { terms, periodId, host };
            const { data } = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement/generate/group", params),
                {
                    success: `${t("Statements.BPSendingSuccessful")}`,
                    pending: `${t("Statements.BPSendingPending")}`,
                    error: `${t("Statements.BPSendingError")}`,
                },
            );
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        } finally {
            setTimeout(() => {
                dispatch(setSendStatementsIdleStatus());
            });
        }
    },
);
export const fetchSendBeneficiarStatement = createAsyncThunk(
    "management/fetchSendBeneficiarStatement",
    async (
        {
            beneficiars,
            periodId,
            host,
        }: {
            beneficiars: number[];
            periodId: number;
            host: string;
        },
        { dispatch, rejectWithValue },
    ) => {
        try {
            const { data } = await toast.promise(
                axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/statement/byBeneficiar/generate/group", {
                    beneficiars,
                    periodId,
                    host,
                }),
                {
                    success: `${t("Statements.BPSendingSuccessful")}`,
                    pending: `${t("Statements.BPSendingPending")}`,
                    error: `${t("Statements.BPSendingError")}`,
                },
            );
            return data;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        } finally {
            setTimeout(() => {
                dispatch(setSendStatementsIdleStatus());
            });
        }
    },
);
export const fetchCloseTerm = createAsyncThunk(
    "management/fetchCloseTerm",
    async ({ periodId, selectedRows }: { periodId: number; selectedRows: number[] }, { dispatch, rejectWithValue }) => {
        try {
            const response = await axios.post(import.meta.env.REACT_APP_API_ENDPOINT + "/term/close", {
                periodId,
                terms: selectedRows,
            });
            // prevent from refreshing so that it doesn't put the newly closed term at the end of table, but not sure if fetching again periods is needed
            if (response.status === 200) {
                dispatch(fetchPeriods());
            }
            return selectedRows;
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        } finally {
            setTimeout(() => {
                dispatch(setSendStatementsIdleStatus());
            });
        }
    },
);

export const downloadDAPFile = createAsyncThunk(
    "management/downloadDAPFile",
    async ({ periodId }: { periodId: number }, { rejectWithValue }) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/management/export-dap`, {
                    params: { periodId },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                },
            );

            downloadApiExport(response.data, `export_DAP_${getDateForFileName()}.csv`);
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

export const fetchCustomExportActions = createAsyncThunk(
    "management/fetchCustomExportActions",
    async (_, { rejectWithValue }) => {
        try {
            const res = await axios.get(`${import.meta.env.REACT_APP_API_ENDPOINT}/custom-export/actions`);
            // A 200 that is not the expected list (e.g. an HTML page from a proxy) would break `.map` in the menu.
            if (!Array.isArray(res.data)) return rejectWithValue(res.data);
            return res.data as string[];
        } catch (e: any) {
            return rejectWithValue(e.response?.data);
        }
    },
);

export const downloadCustomizedExport = createAsyncThunk(
    "management/downloadCustomizedExport",
    async (
        {
            periodId,
            action,
        }: {
            periodId: number | undefined;
            action: string;
        },
        { rejectWithValue },
    ) => {
        try {
            const payload = {
                periodId,
                action,
            };
            const response = await toast.promise(
                axios.post(`${import.meta.env.REACT_APP_API_ENDPOINT}/custom-export`, payload),
                {
                    pending: `${t("Statements.CustomizedPending")}`,
                    success: `${t("Statements.CustomizedSuccessful")}`,
                    error: t("Statements.CustomizedError"),
                },
            );

            const fileName = `bulk_export_${periodId}_${new Date().getTime()}.csv`;
            const linkSource = `data:text/csv;base64,${response.data}`;
            downloadFile({ source: linkSource, fileName, isLink: true });

            return response.data;
        } catch (error: any) {
            return rejectWithValue(error?.response?.data);
        }
    },
);

export const downloadExpensesAccountingFile = createAsyncThunk(
    "management/downloadExpensesAccountingFile",
    async (
        {
            periodId,
            type,
            isZip = false,
        }: {
            periodId: number;
            type: Extract<AccountingMapping["exportType"], "allowance" | "advanceReduction">;
            /**
             * Cegid GECT companies receive the xlsx and the GECT file zipped
             */
            isZip?: boolean;
        },
        { rejectWithValue },
    ) => {
        try {
            const response = await toast.promise(
                axios.get(import.meta.env.REACT_APP_API_ENDPOINT + `/management/expenses/export`, {
                    params: { periodId, type },
                }),
                {
                    pending: `${t("Export.Pending")}`,
                    success: `${t("Export.Successful")}`,
                },
            );

            const label = type === "allowance" ? "provisions" : "diminution_min_garanti";

            const fileName = `export_${label}_${getDateForFileName()}.${isZip ? "zip" : "xlsx"}`;
            const linkSource = `data:${isZip ? "application/zip" : "application/vnd.openxmlformats"};base64,${
                response.data
            }`;
            downloadFile({ source: linkSource, fileName, isLink: true });
        } catch (error: any) {
            return rejectWithValue(error.response.data);
        }
    },
);

type TAddSales = {
    titleId?: string | number;
    termId: number;
    channelId: string | number;
    date: string;
    periodId: string | number;
    collectiveManagementKind?: string;
    /**
     * Columns a contract can be calculated on, monetary ones in cents. A sale can fill several of
     * them at once and the API derives from them the values it stores; turnover_1 / turnover_2 are
     * only sent when the company named them.
     */
    quantity?: number | null;
    turnover?: number | null;
    turnover_1?: number | null;
    turnover_2?: number | null;
};
type TDeleteSales = {
    titleId?: string | number;
    salesId: string | number;
    periodId: string | number;
    channelId: string | number;
    termId: string | number;
};
export type TUpdateSale = {
    titleId?: number;
    salesId: number;
    channelId: number;
    note: string | null;
};
export interface ITerm {
    id: number;
    termId: number;
    title: string;
    titleId: number | null;
    workId?: number | null;
    author: string;
    firstname?: string;
    lastname?: string;
    beneficiarId: number;
    representative: string;
    representativeId?: number;
    amount: string;
    balancePrev: string;
    isbn: string | null;
    quantity: string;
    releaseDate: string;
    creationDate: string;
    endDate: Date | null;
    additionalRights: string;
    collectiveManagement: string;
    hasPostCloseCollectiveManagementSales: number;
    audiovisualAdaption: string;
    other: string;
    grossDuty: string;
    isClosed: number;
    hasClosed?: boolean;
    balance: string;
    entity?: TEntity;
    /**
     * Initial advance paid to beneficiary
     */
    initialAdvance: number;
    /**
     * Initial opening balance when term was created on Crealo
     */
    advancePayment: number;
    channelData: {
        channelId: number;
        channelName: string;
        channelKind: number;
        channelType: number;
        calculationBaseColumn?: CalculationBaseColumn;
        milestoneBaseColumn?: CalculationBaseColumn;
        formatTypeId?: number | null;
        data: string;
    }[];
    email?: string;
    phoneNumber?: string | null;
    notesCount?: number;
    termExistsOnClosedPeriod: number;
    lastSentAt?: Date | null;
    underTheMinimum?: boolean;
    precount: string | number;
    contributions?: number;
    reference?: string;
    contractTypology?: string | null;
    customFields?: { customFieldTypeId: number; uuid: string; value: string }[];
    compensate: number;
    initialProvision?: number;
    provisionPercent?: number;
    accountStatus?: string;
    originalFilemime?: string | null;
    originalFilename?: string | null;
    originalFilesize?: string | null;
    titleAvatarUrl?: string | null;
    avatarUrl?: string | null;
    beneficiaryAvatarUrl?: string;
    compensationGroupId?: number | null;
    periodTypeId?: number | null;
    groupBalance?: number;
    groupGrossDuty?: number;
    groupNextBalance?: number;
    advancePaymentId: number;
    storageId?: string;
    /**
     * (Active when initial Advance is -> rights generated from publication to date when term is created)
     */
    cumulativeRights: number;
    computeFromRepresentative: number;
    hasEnded?: boolean;
    /**
     * ID of period on which term was created.
     */
    originalPeriod: number;
    /** Empty / omitted = every article of the work. */
    coveredTitleIds?: number[];
    /**
     * Whether the title was published before the period start (persisted on term).
     */
    isTitlePublishedBeforePeriod: Binary;
    /**
     * First term_period_data ID for this term.
     */
    originalTPD: number;
}

export type GeneralTabTerm = DTOTermPeriodData & {
    accountStatus: TAccountStatus;
    author: string;
    balancePrev: number;
    beneficiarId: number;
    beneficiaryAvatarUrl: string;
    beneficiaryCode: string;
    beneficiaryTags: [];
    brand: "Par défaut";
    compensationGroupId: number;
    computeFromRepresentative: number | null;
    customStatementTemplateId: null;
    email: string | null;
    groupBalance: number;
    groupGrossDuty: number;
    groupNextBalance: number;
    /**
     * 0 | 1
     * Determines if a term has even just one advance slice linked to it.
     */
    hasAdvances: number;
    /** 0 | 1 — term has at least one collective management channel (kind=4) */
    hasCollectiveManagementChannel: number;
    isClosed: number;
    isbn: string;
    /** EAN of `term.titleId` only — null on work-only terms. */
    legacyTermTitleEan13: string | null;
    /** Real `term.titleId` — null on work-only terms. */
    legacyTermTitleId: number | null;
    netValues: Record<string, number>;
    originalPeriod: number;
    precount: boolean;
    provisionPercent: number;
    quantity: number;
    reference: string;
    releaseDate: string | null;
    representative: string;
    shortNote: string;
    title: string;
    titleAvatarUrl: string;
    titleId: number | null;
    workId?: number | null;
    titleTags: [];
    tpdID: number;
};

export interface DTOTermPeriodData {
    id: number;
    termId: number;
    periodId: number;
    balance: number;
    isActive: boolean;
    paidStatus: PaymentStatus;
    lastSentAt: Date;
    grossDuty: number;
    netToPay: number;
    vat: number;
    withheldVat: number;
    contributions: number;
    nextBalance: number;
    underTheMinimum: boolean;
    physical: number;
    additionalRights: number;
    audiovisualAdaptation: number;
    collectiveManagement: number;
    hasPostCloseCollectiveManagementSales: number;
    other: number;
    initialProvision: number;
    currentProvision: number;
    reinstatedProvisionPercent: number;
    withheldForeign: number;
    paymentDate: Date;
    companyId: number;
    modified: Date;
    /**
     * Advance payment linked to TPD (either from term creation or update of balance).
     * Helps us know if an advance was created on current active period.
     */
    advancePaymentId: number;
    /**
     * Standalone advance slices paid during this period, in cents (frozen on period closure).
     */
    advanceAmount: number;
    /**
     * If they have manually changed data.
     */
    balanceModified: boolean;
    /**
     * Comment they must add when modifying balance.
     */
    comment: string | null;
}

export type TPriceSource = "defaultPrice" | "overridenPrice" | "channelPrice" | "countryPrice";
export interface ISalesByChannels {
    date: Date;
    name: string;
    amount: number;
    type: number;
    kind: number;
    created: Date;
    note?: string;
    id: number;
    returnedSales: number;
    source: string | null;
    channelId: number;
    countryMarket: string;
    titleId?: number;
    overridenPrice: number | null;
    overridenPriceCurrency: Currencies;
    alternativeMilestoneAmount: number | null;
    /** Calculation-base columns a sale line fills; the contract picks the one it computes on. */
    quantity: number | null;
    turnover: number | null;
    turnover_1: number | null;
    turnover_2: number | null;
    computedRevenue: number;
    distributor: string;
    effectivePrice?: number;
    exchangeRate: number;
    priceSource: TPriceSource;
    collectiveManagementKind: string | null;
}

export interface ITermBeneficiar {
    id: number;
    name: string | null;
    surname: string;
    avatar?: string;
    accountStatus?: string;
    paidStatus: number;
    grossDuty: number;
    rightsGross: number;
    contributions: number;
    netToPay: number;
    email?: string;
    lastSentAt?: Date | null;
    precount: string | number;
    vat: string | number;
    withheldPercent?: number;
    avatarUrl?: string;
    beneficiaryName: string;
    customStatementTemplateId?: number;
    paymentDate: string | Date;
    /**
     * Net amount left to pay on period.
     */
    stageAndCommitted: number | null;
    validBankInfos: number;
    acceptedPayments: string;
    bankingMetadata: Record<string, string> | null;
    bankingStatus: string;
    paidAmount: number;
    unpaidAmount: number;
    stagedAmount: number;
    bankCheck: number;
    iban: string;
    paypalEmail: string;
    underTheMinimum: Binary;
    lang: string;
    hasPostCloseCollectiveManagementSales: number;
}
export interface IStats {
    beneficiarsCount: number;
    titlesCount: number;
    grossDuty: number;
    netToPay: number;
    vat: number;
    withheldVat: number;
    contributions: number;
    nbTitles?: number;
    nbUnpaidArticles?: number;
    netRemainingToPay: number;
    initialProvision: number;
    currentProvision: number;
}
export interface TermState {
    salesByChannel: ISalesByChannels[];
    salesByChannelRequestId?: string;
    salesByChannelsStatus: TFetchStatus;
    sendStatementsStatus: TFetchStatus;
    statsStatus: TFetchStatus;
    stats: IStats | null;
    changePaymentStatus: number[];
    exportFileStatus: TFetchStatus;
    addSalesCheckingStatus: number[];
    addSalesPendingStatus: number[];
    closeTermsStatus: TFetchStatus;
    closeTermStatus: TFetchStatus;
    generalTabData: GeneralTabTerm[];
    beneficiaries: ITermBeneficiar[];
    unpaidBeneficiaries: ITermBeneficiar[];
    fullyUnpaidBeneficiaries: ITermBeneficiar[];
    partiallyPaidBeneficiaries: ITermBeneficiar[];
    termsLoadingStatus: TFetchStatus;
    benefInfoLoadingStatus: TFetchStatus;
    needRefresh: boolean;
    fetchTermInfoRequestId: string;
    customExportActions: string[];
    customExportActionsStatus: TFetchStatus;
    recomputeRoyaltiesJobId: number | undefined;
    recomputeRoyaltiesPeriodId: number | undefined;
}

const initialState: TermState = {
    salesByChannel: [],
    salesByChannelRequestId: undefined,
    salesByChannelsStatus: "idle",
    sendStatementsStatus: "idle",
    statsStatus: "idle",
    stats: null,
    changePaymentStatus: [],
    exportFileStatus: "idle",
    addSalesPendingStatus: [],
    addSalesCheckingStatus: [],
    closeTermsStatus: "idle",
    closeTermStatus: "idle",
    generalTabData: [],
    beneficiaries: [],
    unpaidBeneficiaries: [],
    fullyUnpaidBeneficiaries: [],
    partiallyPaidBeneficiaries: [],
    termsLoadingStatus: "idle",
    benefInfoLoadingStatus: "idle",
    needRefresh: false,
    fetchTermInfoRequestId: "",
    customExportActions: [],
    customExportActionsStatus: "idle",
    recomputeRoyaltiesJobId: undefined,
    recomputeRoyaltiesPeriodId: undefined,
};

export const termsSlice = createSlice({
    name: "beneficiaries",
    initialState,
    reducers: {
        setCloseTermIdleStatus(state) {
            state.closeTermsStatus = "idle";
        },
        setExportFileIdleStatus(state) {
            state.exportFileStatus = "idle";
        },
        setStatsIdleStatus(state) {
            state.statsStatus = "idle";
        },
        setRoyaltiesRecalculating(state, action: PayloadAction<{ periodId: number; jobId?: number }>) {
            if (action.payload.jobId !== undefined) {
                state.recomputeRoyaltiesJobId = action.payload.jobId;
            }
            state.recomputeRoyaltiesPeriodId = action.payload.periodId;
            state.statsStatus = "pending";
            state.termsLoadingStatus = "pending";
            state.benefInfoLoadingStatus = "pending";
        },
        clearRoyaltiesRecalculating(state) {
            state.recomputeRoyaltiesJobId = undefined;
            state.recomputeRoyaltiesPeriodId = undefined;
        },
        setRecalculatingPendingStatus(state) {
            state.statsStatus = "pending";
            state.termsLoadingStatus = "pending";
            state.benefInfoLoadingStatus = "pending";
        },
        setSendStatementsIdleStatus(state) {
            state.sendStatementsStatus = "idle";
        },
        setLastSentAtTerms(state, action) {
            const currentTerms = JSON.parse(JSON.stringify(state.generalTabData));
            // let termIndex = currentTerms.findIndex((item: any) => action.payload?.includes(item.id) && item.isClosed == 0)
            const termIndexes: number[] = [];
            let i = 0;
            for (const term of currentTerms) {
                if (action.payload?.includes(term.id) && term.email) termIndexes.push(i);
                i++;
            }
            // currentTerms[termIndex]["hasClosed"] = true
            termIndexes.forEach((item) => (currentTerms[item]["lastSentAt"] = new Date().toString()));
            state.generalTabData = currentTerms;
        },
        setLastSentAtBeneficiar(state, action) {
            const currentBeneficiars = JSON.parse(JSON.stringify(state.beneficiaries));
            const beneficiarIndexes: number[] = [];
            let i = 0;
            for (const beneficiar of currentBeneficiars) {
                if (action.payload?.includes(beneficiar.id) && beneficiar.email) beneficiarIndexes.push(i);
                i++;
            }
            beneficiarIndexes.forEach((item) => (currentBeneficiars[item]["lastSentAt"] = new Date().toString()));
            state.beneficiaries = currentBeneficiars;
        },
        updateBeneficiariesPaymentStatus(state, { payload }) {
            const currentBeneficiaries = [...state.beneficiaries];
            const newElements: { elements: INewPaymentData[]; status: PaymentStatus } = payload;

            newElements.elements.forEach((item) => {
                const currentBeneficiary = currentBeneficiaries.find((el) => el.id === item.id);
                if (currentBeneficiary) {
                    const status = payload.status;

                    // Update stageAndCommitted (full amount either paid or for bank transfer)
                    currentBeneficiary.stageAndCommitted =
                        (currentBeneficiary.stageAndCommitted ?? 0) + +(item.amount ?? 0);

                    if (status === PaymentStatus.PAID) {
                        currentBeneficiary.paidAmount += +(item.amount ?? 0);
                    } else {
                        currentBeneficiary.stagedAmount += +(item.amount ?? 0);
                    }
                }
            });

            state.beneficiaries = currentBeneficiaries;
        },
        validateBeneficiariesBankInfo(state, { payload }) {
            const currentBeneficiaries = [...state.beneficiaries];
            state.beneficiaries = currentBeneficiaries.map((item) => {
                if (item.id === payload.id) {
                    return {
                        ...item,
                        validBankInfos: 1,
                        acceptedPayments: item.acceptedPayments?.includes("rib") ? item.acceptedPayments : "rib",
                    };
                } else {
                    return item;
                }
            });
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getGeneralTabData.fulfilled, (state, action) => {
            state.generalTabData = action.payload.entries;
            const requestId = action.meta.requestId;
            // waiting for last fetch request executed to set loading status to succeed
            if (!isFinalReq(state.fetchTermInfoRequestId, requestId)) return;
            state.termsLoadingStatus = "succeeded";
            state.needRefresh = false;
        });
        builder.addCase(getGeneralTabData.pending, (state, action) => {
            const requestId = action.meta.requestId;
            state.fetchTermInfoRequestId = requestId;
            state.generalTabData = [];
            state.termsLoadingStatus = "pending";
        });
        builder.addCase(getGeneralTabData.rejected, (state, action) => {
            if (!isFinalReq(state.fetchTermInfoRequestId, action.meta.requestId)) return;
            state.termsLoadingStatus = "failed";
            const translation = translationsWithFallback(
                (action.payload as any)?.message,
                "ServerError.InternalServerError",
            );
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchBeneficiariesInformation.fulfilled, (state, action) => {
            state.beneficiaries = (action.payload as any).entries;
            state.benefInfoLoadingStatus = "succeeded";
        });
        builder.addCase(fetchBeneficiariesInformation.pending, (state, action) => {
            state.benefInfoLoadingStatus = "pending";
            state.beneficiaries = [];
        });
        builder.addCase(fetchBeneficiariesInformation.rejected, (state, { payload }) => {
            state.benefInfoLoadingStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchUnpaidBeneficiariesInformation.fulfilled, (state, action) => {
            const entries = action.payload.entries;
            state.unpaidBeneficiaries = entries;
            // Partially paid beneficiaries must not trigger the close-period carry-forward step.
            state.fullyUnpaidBeneficiaries = entries.filter((benef) => benef.stageAndCommitted === 0);
            state.partiallyPaidBeneficiaries = entries.filter(
                (benef) => benef.stageAndCommitted != null && benef.stageAndCommitted > 0,
            );
        });
        builder.addCase(fetchUnpaidBeneficiariesInformation.pending, (state, action) => {
            state.unpaidBeneficiaries = [];
            state.fullyUnpaidBeneficiaries = [];
            state.partiallyPaidBeneficiaries = [];
        });
        builder.addCase(fetchUnpaidBeneficiariesInformation.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchCloseTerm.fulfilled, (state, action) => {
            toast.success(`${t("Management.SuccessfullCloseTerm")}`);
            state.closeTermsStatus = "succeeded";
            const closedIds = new Set(action.payload ?? []);
            state.generalTabData = state.generalTabData.map((term) =>
                closedIds.has(term.id) && term.isClosed === 0 ? { ...term, hasClosed: true } : term,
            );
        });
        builder.addCase(fetchCloseTerm.pending, (state) => {
            state.closeTermsStatus = "pending";
        });
        builder.addCase(fetchCloseTerm.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.closeTermsStatus = "failed";
        });

        builder.addCase(fetchAddSales.fulfilled, (state, action) => {
            state.addSalesPendingStatus = state.addSalesPendingStatus.filter((item) => item !== action.meta.arg.termId);
            const updatedById = new Map<number, GeneralTabTerm>(
                action.payload.entries.map((item: GeneralTabTerm) => [item.id, item]),
            );
            state.generalTabData = state.generalTabData.map((term) => updatedById.get(term.id) ?? term);
            toast.success(`${t("Management.SuccessfullAddSales")}`);
        });
        builder.addCase(recomputeTermRoyalties.fulfilled, (state, action) => {
            const updatedEntries = action.payload?.entries ?? [];
            if (updatedEntries.length) {
                const updatedById = new Map<number, GeneralTabTerm>(
                    updatedEntries.map((item: GeneralTabTerm) => [item.id, item]),
                );
                state.generalTabData = state.generalTabData.map((term) => updatedById.get(term.id) ?? term);
            }
            toast.success(`${t("Management.RecomputeTermRoyaltiesSuccess")}`);
        });
        builder.addCase(recomputeTermRoyalties.rejected, (_state, action) => {
            const translation = translationsWithFallback(
                (action.payload as any)?.message,
                "Management.RecomputeRoyaltiesFailed",
            );
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchAddSales.pending, (state, action) => {
            state.addSalesPendingStatus = [...state.addSalesPendingStatus, action.meta.arg.termId];
        });
        builder.addCase(fetchAddSales.rejected, (state, action) => {
            state.addSalesPendingStatus = [...state.addSalesPendingStatus].filter(
                (item) => item !== action.meta.arg.termId,
            );
            const translation = translationsWithFallback(
                (action.payload as any)?.message,
                "ServerError.InternalServerError",
            );
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchDeleteSales.fulfilled, (state, action) => {
            toast.success(`${t("Management.SuccessfullDeleteSales")}`);
            const currentSales = JSON.parse(JSON.stringify(state.salesByChannel));
            state.salesByChannel = currentSales.filter((sale: any) => sale.id != action.payload.id);
            state.needRefresh = true;
        });
        builder.addCase(fetchDeleteSales.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(updateSale.fulfilled, (state, action) => {
            toast.success(`${t("Management.SuccessfullUpdateSaleNote")}`);
            state.salesByChannel = state.salesByChannel.map((sale) =>
                sale.id === action.meta.arg.salesId ? { ...sale, note: action.meta.arg.note ?? "" } : sale,
            );
        });
        builder.addCase(updateSale.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchCheckAddingSales.fulfilled, (state, action) => {
            state.addSalesCheckingStatus = state.addSalesCheckingStatus.filter(
                (item) => item !== action.meta.arg.termId,
            );
        });
        builder.addCase(fetchCheckAddingSales.pending, (state, action) => {
            state.addSalesCheckingStatus = [...state.addSalesCheckingStatus, action.meta.arg.termId];
        });
        builder.addCase(fetchCheckAddingSales.rejected, (state, action) => {
            state.addSalesCheckingStatus = state.addSalesCheckingStatus.filter(
                (item) => item !== action.meta.arg.termId,
            );
            toast.error(`${t("ServerError.CheckSalesFalse")}`, { autoClose: 5000 });
        });

        builder.addCase(updatePaymentStatus.pending, (state, action) => {
            state.changePaymentStatus = [...state.changePaymentStatus, action.meta.arg.id as number];
        });
        builder.addCase(updatePaymentStatus.fulfilled, (state, action) => {
            toast.success(`${t("Management.SuccessfullChangePaymentStatus")}`);
            state.changePaymentStatus = state.changePaymentStatus.filter((item) => item !== action.meta.arg.id);
            const currentBeneficiaries = JSON.parse(JSON.stringify(state.beneficiaries));
            const index = currentBeneficiaries.findIndex((item: any) => item.id == (action.payload as any).id);
            currentBeneficiaries[index].paidStatus = (action.payload as any).status;
            currentBeneficiaries[index].paymentDate = action.meta.arg.paymentDate;
            state.beneficiaries = currentBeneficiaries;
        });
        builder.addCase(updatePaymentStatus.rejected, (state, action) => {
            state.changePaymentStatus = state.changePaymentStatus.filter((item) => item !== action.meta.arg.id); // TODO : remove this ?
            const translation = translationsWithFallback(
                (action.payload as any)?.message,
                "ServerError.InternalServerError",
            );
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(updatePaymentStatusBulk.pending, () => {});
        builder.addCase(updatePaymentStatusBulk.fulfilled, (state, action) => {
            state.beneficiaries = [...state.beneficiaries].map((item) => {
                if (action.payload.id.includes(item.id)) {
                    return { ...item, paidStatus: action.payload.status, paymentDate: action.meta.arg.paymentDate };
                } else {
                    return item;
                }
            });
            toast.success(`${t("Management.SuccessfullChangePaymentStatus")}`);
        });
        builder.addCase(updatePaymentStatusBulk.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });

        builder.addCase(fetchExportFile.pending, (state) => {
            state.exportFileStatus = "pending";
        });
        builder.addCase(fetchExportFile.fulfilled, (state) => {
            state.exportFileStatus = "succeeded";
        });
        builder.addCase(fetchExportFile.rejected, (state) => {
            state.exportFileStatus = "failed";
        });
        builder.addCase(fetchStats.pending, (state) => {
            state.statsStatus = "pending";
        });
        builder.addCase(fetchStats.fulfilled, (state, action: PayloadAction<IStats>) => {
            state.statsStatus = "succeeded";
            state.stats = action.payload;
        });
        builder.addCase(fetchStats.rejected, (state, { payload }) => {
            state.statsStatus = "failed";
            state.stats = null;
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchPressStats.fulfilled, (state, action: PayloadAction<IStats>) => {
            state.statsStatus = "succeeded";
            state.stats = action.payload;
        });
        builder.addCase(fetchPressStats.rejected, (state, { payload }) => {
            state.statsStatus = "failed";
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(fetchSendStatement.pending, (state) => {
            state.sendStatementsStatus = "pending";
        });
        builder.addCase(fetchSendStatement.fulfilled, (state) => {
            toast.success(`${t("Management.StatementsSuccessfullySent")}`);
            state.sendStatementsStatus = "succeeded";
        });
        builder.addCase(fetchSendStatement.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.sendStatementsStatus = "failed";
            state.stats = null;
        });
        builder.addCase(fetchSendBeneficiarStatement.pending, (state) => {
            state.sendStatementsStatus = "pending";
        });
        builder.addCase(fetchSendBeneficiarStatement.fulfilled, (state) => {
            toast.success(`${t("Management.StatementsSuccessfullySent")}`);
            state.sendStatementsStatus = "succeeded";
        });
        builder.addCase(fetchSendBeneficiarStatement.rejected, (state) => {
            toast.error(`${t("Management.FailedToSendStatements")}`);
            state.sendStatementsStatus = "failed";
        });
        builder.addCase(fetchSalesByChannel.pending, (state, action) => {
            state.salesByChannelsStatus = "pending";
            state.salesByChannel = [];
            state.salesByChannelRequestId = action.meta.requestId;
        });
        builder.addCase(fetchSalesByChannel.fulfilled, (state, action) => {
            if (state.salesByChannelRequestId !== action.meta.requestId) return;
            state.salesByChannelsStatus = "succeeded";
            state.salesByChannel = action.payload.entries;
        });
        builder.addCase(fetchSalesByChannel.rejected, (state, action) => {
            if (state.salesByChannelRequestId !== action.meta.requestId) return;
            const translation = translationsWithFallback(
                (action.payload as { message?: string })?.message ?? "",
                "ServerError.InternalServerError",
            );
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
            state.salesByChannelsStatus = "failed";
            state.stats = null;
        });
        builder.addCase(fetchCustomExportActions.pending, (state) => {
            state.customExportActionsStatus = "pending";
        });

        builder.addCase(fetchCustomExportActions.fulfilled, (state, action) => {
            state.customExportActionsStatus = "succeeded";
            state.customExportActions = action.payload;
        });

        builder.addCase(fetchCustomExportActions.rejected, (state) => {
            state.customExportActionsStatus = "failed";
            state.customExportActions = [];
        });
        builder.addCase(updateInitialBalance.fulfilled, (state, action) => {
            const payload = action.payload;

            const newData = [...state.generalTabData].map((term) => {
                if (term.id === payload.termId) {
                    return {
                        ...term,
                        balancePrev: payload.balance,
                        balance: payload.nextBalance,
                        grossDuty: payload.grossDuty,
                        balanceModified: !!payload.balanceModified,
                        advancePaymentId: payload.advancePaymentId,
                        groupBalance: payload.groupBalance,
                        groupNextBalance: payload.groupNextBalance,
                        hasAdvances: payload.hasAdvances ?? term.hasAdvances,
                        advanceAmount: payload.advanceAmount ?? term.advanceAmount ?? 0,
                    };
                } else {
                    return term;
                }
            });
            state.generalTabData = newData as GeneralTabTerm[];
            toast.success(`${t("BalanceUpdate.UpdateSuccess")}`);
        });
        builder.addCase(updateInitialBalance.rejected, (_, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "ServerError.InternalServerError");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
        builder.addCase(downloadDAPFile.rejected, (state, { payload }) => {
            const translation = translationsWithFallback((payload as any)?.message, "Export.Error");
            toast.error(`${translation}`, { toastId: "ServerError.InternalServerError" });
        });
    },
});
export const {
    setCloseTermIdleStatus,
    setExportFileIdleStatus,
    setStatsIdleStatus,
    setRoyaltiesRecalculating,
    clearRoyaltiesRecalculating,
    setRecalculatingPendingStatus,
    setSendStatementsIdleStatus,
    setLastSentAtTerms,
    setLastSentAtBeneficiar,
    updateBeneficiariesPaymentStatus,
    validateBeneficiariesBankInfo,
} = termsSlice.actions;
export default termsSlice.reducer;
